/** The record page's tablist — **one implementation, every kind** (EXP-39).
 *
 * It was inline in `Record.tsx`, which was fine while one page had tabs. The moment a risk, a
 * source and an initiative got the same page, the choice was to copy forty lines of ARIA or to
 * share them, and a copy is how a second tablist comes to be a `<nav>` of links that announces
 * nothing. The accessible behaviour is not decoration here: without it a listener cannot tell a tab
 * that worked from one that did not, so they press it again.
 *
 * Three parts people leave out, and each does real work:
 *
 * - **Roving `tabIndex`.** Only the selected tab is in the tab order; the rest are reachable by
 *   arrow key. Without it a five-tab set is five tab stops wearing the right roles, and a keyboard
 *   reader pays five presses to get past them on every visit.
 * - **Arrow keys wrap**, which is what the pattern specifies. Stopping at the ends makes the last
 *   tab a dead end.
 * - **A live region that exists from the first render.** Activating a tab leaves focus on the
 *   button and silently replaces the panels below it. One added at the moment it has something to
 *   say is frequently never announced at all.
 */

import type { ReactNode } from "react";

import own from "./Record.module.css";

export type TabSpec = { readonly key: string; readonly label: string };

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export function RecordTabs({
  tabs,
  tab,
  onSelect,
  label,
  saying,
}: {
  tabs: readonly TabSpec[];
  tab: string;
  onSelect: (key: string) => void;
  /** What the set of tabs is for, for a screen reader: "Record", "Climate risk". */
  label: string;
  /** What the live region says — the tab's name **and what it is about**, because "Showing
   * Workflow" does not tell a listener which record's workflow they are on. */
  saying: ReactNode;
}) {
  return (
    <>
      <div
        className={cls(own.tabs)}
        role="tablist"
        aria-label={label}
        onKeyDown={(event) => {
          const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
          if (step === 0) return;
          event.preventDefault();
          const at = tabs.findIndex((entry) => entry.key === tab);
          const next = tabs[(at + step + tabs.length) % tabs.length];
          // `noUncheckedIndexedAccess` is on, and it is right to be: `at` is -1 when the URL
          // carries a tab nothing matches, and -1 + -1 wraps to a real index only by luck.
          if (!next) return;
          onSelect(next.key);
          document.getElementById(`tab-${next.key}`)?.focus();
        }}
      >
        {tabs.map((entry) => (
          <button
            key={entry.key}
            id={`tab-${entry.key}`}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            aria-controls={`panel-${entry.key}`}
            tabIndex={tab === entry.key ? 0 : -1}
            className={cls(own.tab, tab === entry.key && own.tabOn)}
            onClick={() => onSelect(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>
      <p className="visuallyHidden" role="status" aria-live="polite" data-testid="record-tab-live">
        {saying}
      </p>
    </>
  );
}

/** The one panel that is showing, carrying the id its tab points at.
 *
 * Rendering every tab and hiding all but one would put four screens' worth of content in the
 * accessibility tree and four screens' worth of queries on every visit — the register's own lesson
 * (EXP-39's render cap) one page over.
 *
 * Focusable, because a panel whose content has no focusable element of its own leaves a keyboard
 * reader with nowhere to go after activating the tab.
 */
export function RecordPanel({
  tab,
  children,
  className,
}: {
  tab: string;
  children: ReactNode;
  /** The layout of what the panel holds. The record page's panels are placed on the twelve columns
   * (`column="1 / 7"`), so its panel is itself a twelve-column grid (`Record.module.css`'s
   * `tabGrid`); screens that stack their tab's contents wrap the panel in their own `tabArea`. */
  className?: string;
}) {
  return (
    <div
      id={`panel-${tab}`}
      role="tabpanel"
      aria-labelledby={`tab-${tab}`}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  );
}
